import { urlDecode } from '@utils/helper/urlEncodeDecode';
import { timeStampToDate } from '@utils/index';
import dayjs from 'dayjs';
import { useQuery } from 'react-query';

import { baseErrorHandler } from '../../../queries/baseErrorHandler';
import { $api } from '../../../service/RequestService';
import { DATE_FORMAT } from '../../../utils/constants/format';
import { getPaymentsBody } from '../../../utils/helper/getSearchAndDownloadBodyAllTables';
import { ContractDataForCreatePayment } from '../../../utils/models/ContractDataForCreatePayment';
import { Pagination } from '../../../utils/models/Pagination';
import { PaymentList } from '../../../utils/models/PaymentList';
import { PaymentSum } from '../../../utils/models/PaymentSum';
import { SearchParamsPayment } from '../../payment/Payment';
import { endPoints } from '../utils/constants/endPoints';
import { queryKeys } from '../utils/constants/queryKeys';

// get payments list
export function usePaymentsList(size: number, searchObj: SearchParamsPayment) {
  return useQuery<Pagination<PaymentList>>(
    [queryKeys.PAYMENT, size, searchObj.page, JSON.stringify(searchObj)],
    async () => {
      const res = await $api.post(endPoints.PAYMENT_FILTER, getPaymentsBody(size, searchObj));

      const data: Pagination<PaymentList> = res?.data?.data;

      return data
        ? {
            ...data,
            data: data?.data?.map(item => ({
              ...item,
              createDate: timeStampToDate({ date: item?.createDate, timeStamp: item?.createTimestamp }),
              canceledDate: timeStampToDate({ date: item?.canceledDate, timeStamp: item?.canceledTimestamp })
            }))
          }
        : data;
    }
  );
}

export function usePaymentsSum(searchObj: SearchParamsPayment) {
  // 🥵😭 qaysi gunohlarim uchuna
  const newSearchObj = { ...searchObj };
  const types: string[] = [];
  const paymentCustomTypeIds: string[] = [];
  const bases: string[] = [];
  const buildings: string[] = [];
  const currencies: string[] = [];
  const houses: string[] = [];
  const contractStatus: string[] = [];
  const debits: string[] = [];
  const fieldValue: string[] = [];
  const nonResidentialType: string[] = [];
  const responsibleIdsContract: number[] = [];
  const paymentWays: string[] = [];

  if (Array.isArray(newSearchObj.nonResidentialType)) {
    newSearchObj.nonResidentialType.forEach(id => {
      nonResidentialType.push(urlDecode(id));
    });
  } else if (newSearchObj.nonResidentialType) {
    nonResidentialType.push(urlDecode(newSearchObj.nonResidentialType));
  }

  if (Array.isArray(newSearchObj.fieldValue)) {
    newSearchObj.fieldValue.forEach(id => {
      fieldValue.push(urlDecode(id));
    });
  } else if (newSearchObj.fieldValue) {
    fieldValue.push(urlDecode(newSearchObj.fieldValue));
  }

  if (Array.isArray(newSearchObj.types)) {
    newSearchObj.types.forEach(id => {
      types.push(id);
    });
  } else if (newSearchObj.types) {
    types.push(newSearchObj.types);
  }

  if (Array.isArray(newSearchObj.paymentCustomTypeIds)) {
    newSearchObj.paymentCustomTypeIds.forEach(id => {
      paymentCustomTypeIds.push(id);
    });
  } else if (newSearchObj.paymentCustomTypeIds) {
    paymentCustomTypeIds.push(newSearchObj.paymentCustomTypeIds);
  }

  if (Array.isArray(newSearchObj.bases)) {
    newSearchObj.bases.forEach(id => {
      bases.push(id);
    });
  } else if (newSearchObj.bases) {
    bases.push(newSearchObj.bases);
  }

  if (Array.isArray(newSearchObj.buildings)) {
    newSearchObj.buildings.forEach(id => {
      buildings.push(id);
    });
  } else if (newSearchObj.buildings) {
    buildings.push(newSearchObj.buildings);
  }

  if (Array.isArray(newSearchObj.currencies)) {
    newSearchObj.currencies.forEach(id => {
      currencies.push(id);
    });
  } else if (newSearchObj.currencies) {
    currencies.push(newSearchObj.currencies);
  }

  if (Array.isArray(newSearchObj.houses)) {
    newSearchObj.houses.forEach(id => {
      houses.push(id);
    });
  } else if (newSearchObj.houses) {
    houses.push(newSearchObj.houses);
  }

  if (Array.isArray(newSearchObj.contractStatus)) {
    newSearchObj.contractStatus.forEach(id => {
      contractStatus.push(id);
    });
  } else if (newSearchObj.contractStatus) {
    contractStatus.push(newSearchObj.contractStatus);
  }

  if (Array.isArray(newSearchObj.debits)) {
    newSearchObj.debits.forEach(id => {
      debits.push(id);
    });
  } else if (newSearchObj.debits) {
    debits.push(newSearchObj.debits);
  }

  if (Array.isArray(newSearchObj.paymentWays)) {
    newSearchObj.paymentWays.forEach(type => {
      paymentWays.push(type);
    });
  } else if (newSearchObj.paymentWays) {
    paymentWays.push(newSearchObj?.paymentWays);
  }

  if (Array.isArray(newSearchObj.responsibleIdsContract)) {
    newSearchObj.responsibleIdsContract.forEach(id => {
      responsibleIdsContract.push(id);
    });
  } else if (newSearchObj.responsibleIdsContract) {
    responsibleIdsContract.push(newSearchObj?.responsibleIdsContract);
  }

  newSearchObj.nonResidentialType = nonResidentialType.length > 0 ? (nonResidentialType as any) : undefined;
  newSearchObj.types = types.length > 0 ? (types as any) : undefined;
  newSearchObj.paymentCustomTypeIds = paymentCustomTypeIds.length > 0 ? (paymentCustomTypeIds as any) : undefined;
  newSearchObj.bases = bases.length > 0 ? (bases as any) : undefined;
  newSearchObj.fieldValue = fieldValue.length > 0 ? (fieldValue as any) : undefined;
  newSearchObj.buildings = buildings.length > 0 ? (buildings as any) : undefined;
  newSearchObj.currencies = currencies.length > 0 ? (currencies as any) : undefined;
  newSearchObj.houses = houses.length > 0 ? (houses as any) : undefined;
  newSearchObj.paymentWays = paymentWays.length > 0 ? paymentWays : undefined;
  newSearchObj.responsibleIdsContract = responsibleIdsContract.length > 0 ? responsibleIdsContract : undefined;

  if (!searchObj.startDate && Object.keys(searchObj).length === 0) {
    newSearchObj.startDate = dayjs(new Date()).format(DATE_FORMAT);
    newSearchObj.finishDate = dayjs(new Date()).format(DATE_FORMAT);
  }

  newSearchObj.contractStatus = contractStatus.length > 0 ? contractStatus : undefined;
  newSearchObj.debits = debits.length > 0 ? debits : undefined;

  return useQuery<PaymentSum>(
    [queryKeys.PAYMENT, 'sum', JSON.stringify(searchObj)],
    async () => {
      const res = await $api.post(endPoints.PAYMENT_FILTER_SUM, newSearchObj);

      return res?.data?.data;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchIntervalInBackground: false,
      refetchInterval: 60000,
      onError: baseErrorHandler,
      retry: false
    }
  );
}

export function useGetContractDataForPayment(contractId: string) {
  const url = `${endPoints.CONTRACT + contractId}/for-payment-plus`;

  return useQuery<ContractDataForCreatePayment>(
    [queryKeys.CONTRACT_DATA_FOR_CREATE_PAYMENT, contractId],
    async () => {
      const res = await $api.get(url);

      return res?.data?.data;
    },
    {
      enabled: !!contractId,
      refetchOnWindowFocus: false,
      refetchInterval: Infinity,
      retry: false,
      onError: baseErrorHandler
    }
  );
}
